import { EORPModal } from '@/features/dashboard/components/EORPModal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { RemoveDocumentConfirmDialog } from '@/components/ui/RemoveDocumentConfirmDialog'
import { PaymentModal } from '@/features/reimbursements/components/PaymentModal'
import { resolveConfigurePaymentInitialAmount } from '@/features/reimbursements/utils/detailPageDerived'
import { PolicyDeclarationFilesPopup } from '@/features/reimbursements/components/PolicyDeclarationFilesPopup'
import { ReviewClaimModal } from '@/features/reimbursements/components/ReviewClaimModal'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { DEFAULT_EVIDENCE_REMOVE_MESSAGE } from '../../constants/detailPage'
import { residentConfirmPresentation } from '../../constants/residentDialogProps'
import { endOfMonthIsoToday, paymentCreatedAtToIsoDate } from '../../utils/detailPageDerived'
import type { ResidentDetailSectionProps } from '../../types/detailPage'
import { GuidanceDialog } from '../dialogs/GuidanceDialog'
import { LeaseEndDateDialog } from '../dialogs/LeaseEndDateDialog'
import { CovidReasonDialog, ReasonDialog } from '../dialogs/ReasonDialog'
import { CancelReimbursementRequestDialog } from '../dialogs/CancelReimbursementRequestDialog'
import { PublicNotesNotifyDialog } from '../dialogs/PublicNotesNotifyDialog'
import { ReviewResidentDialog } from '../dialogs/ReviewResidentDialog'

export function ResidentDetailDialogs({ ctx }: ResidentDetailSectionProps) {
  return (
    <>
      <EORPModal isOpen={ctx.showEorpModal} onClose={() => ctx.setShowEorpModal(false)} />
      <CancelReimbursementRequestDialog
        isOpen={!!ctx.dialogs.cancelReimbursementRequest}
        defaultId={ctx.defaultId}
        onClose={() => ctx.closeDialog('cancelReimbursementRequest')}
        onUpdated={ctx.handleCancelReimbursementSuccess}
      />
      <ReviewResidentDialog
        isOpen={!!ctx.dialogs.reviewDocs}
        defaultId={ctx.defaultId}
        formValues={ctx.values}
        isFinalLedger={ctx.detail.residentDetail?.resident_default?.is_final_ledger}
        onClose={() => ctx.closeDialog('reviewDocs')}
        onUpdated={ctx.reloadDetail}
      />
      {ctx.defaultId ? (
        <ReviewClaimModal
          isOpen={!!ctx.dialogs.reviewClaim}
          defaultId={ctx.defaultId}
          applicationId={ctx.applicationId}
          adjustmentData={ctx.calculationPaymentPayload}
          reviewLogId={ctx.approvalReviewClaim?.reviewLogId}
          approvalType={ctx.approvalReviewClaim?.approvalType}
          approvalTypeValue={ctx.approvalReviewClaim?.approvalTypeValue}
          defaultType={ctx.approvalReviewClaim?.defaultType}
          onClose={() => {
            ctx.closeDialog('reviewClaim')
            ctx.revertApproval()
          }}
          onSuccess={ctx.reloadDetail}
        />
      ) : null}
      <PublicNotesNotifyDialog
        isOpen={ctx.publicNotesNotifyOpen}
        isLibertyRole={ctx.isLibertyRole}
        processing={ctx.publicMessageSaving}
        onClose={() => ctx.setPublicNotesNotifyOpen(false)}
        onSubmit={(selection) => {
          const msg = String(ctx.form.getValues('message') ?? '').trim()
          if (!msg) return
          void ctx.submitPublicNotesMessage(msg, selection)
        }}
      />
      <GuidanceDialog isOpen={!!ctx.dialogs.guidance} defaultId={ctx.defaultId} onClose={() => ctx.closeDialog('guidance')} onUpdated={ctx.reloadDetail} />
      <LeaseEndDateDialog
        isOpen={!!ctx.dialogs.leaseEndDate}
        onClose={ctx.handleLeaseEndDateCancel}
        onSubmit={(moveoutDate) => void ctx.handleLeaseEndDateSubmit(moveoutDate)}
      />
      <CovidReasonDialog
        isOpen={!!ctx.dialogs.covidReason}
        onClose={() => ctx.cancelCovidReason()}
        onSubmit={(note) => ctx.confirmCovidReason(note)}
      />
      <ReasonDialog
        isOpen={!!ctx.dialogs.rejectReason}
        title="Reason"
        onClose={() => ctx.closeDialog('rejectReason')}
        onSubmit={(note) => ctx.confirmRejectClaim(note)}
      />
      <ReasonDialog
        isOpen={!!ctx.dialogs.approvalReason}
        title="Reason"
        onClose={() => ctx.cancelApprovalReason()}
        onSubmit={(note) => ctx.confirmApprovalReason(note)}
      />
      {ctx.defaultId ? (
        <PaymentModal
          isOpen={!!ctx.dialogs.configurePayment}
          onClose={() => ctx.closeDialog('configurePayment')}
          onSuccess={() => void ctx.detail.load({ silent: true })}
          defaultId={ctx.defaultId}
          propertyId={ctx.propertyId}
          initialAmount={resolveConfigurePaymentInitialAmount(
            ctx.calculationPaymentPayload,
            ctx.detail.residentDetail?.resident_default?.net_amount,
          )}
          initialPaymentDate={endOfMonthIsoToday()}
          calculationData={ctx.calculationPaymentPayload}
          adjustmentData={ctx.calculationPaymentPayload}
        />
      ) : null}
      {ctx.defaultId ? (
        <PaymentModal
          isOpen={!!ctx.dialogs.adjustPayment}
          onClose={() => ctx.closeDialog('adjustPayment')}
          onSuccess={() => void ctx.detail.load({ silent: true })}
          mode="adjust"
          defaultId={ctx.defaultId}
          propertyId={ctx.propertyId}
          initialAmount={ctx.adjustPaymentInitialAmount}
          positiveAmount={false}
          previousDefaultPayment={ctx.previousDefaultPayment}
          initialPaymentDate={endOfMonthIsoToday()}
          calculationData={ctx.calculationPaymentPayload}
          adjustmentData={ctx.calculationPaymentPayload}
        />
      ) : null}
      {ctx.defaultId && ctx.editPaymentRow ? (
        <PaymentModal
          isOpen={!!ctx.dialogs.editPayment}
          onClose={() => ctx.closeDialog('editPayment')}
          onSuccess={() => void ctx.detail.load({ silent: true })}
          mode="edit"
          selectedPaymentLogId={ctx.editPaymentRow.payment_log_id}
          defaultId={ctx.defaultId}
          propertyId={ctx.propertyId}
          initialAmount={ctx.editPaymentInitialAmount}
          positiveAmount={Number(ctx.editPaymentRow.payment_amount) > 0}
          initialPaymentDate={
            paymentCreatedAtToIsoDate(ctx.editPaymentRow.created_at) || endOfMonthIsoToday()
          }
          selectedPaymentSeed={{
            check_number: ctx.editPaymentRow.check_number,
            transaction_notes: ctx.editPaymentRow.transaction_notes,
            payment_notes: ctx.editPaymentRow.payment_notes,
          }}
          calculationData={ctx.calculationPaymentPayload}
          adjustmentData={ctx.calculationPaymentPayload}
        />
      ) : null}
      {ctx.confirm ? (
        <ConfirmDialog
          isOpen
          title={ctx.confirm.title}
          message={ctx.confirm.message}
          htmlMessage={ctx.confirm.htmlMessage}
          primaryLabel={ctx.confirm.primaryLabel || 'Save'}
          secondaryLabel={ctx.confirm.secondaryLabel ?? 'Cancel'}
          onPrimary={ctx.confirm.onPrimary}
          onSecondary={ctx.confirm.onSecondary ?? (() => ctx.setConfirm(null))}
          onDismiss={() => ctx.setConfirm(null)}
          {...residentConfirmPresentation}
          messageAlign={ctx.confirm.messageAlign}
          messageWeight={ctx.confirm.messageWeight}
          primaryButtonClassName={ctx.confirm.primaryButtonClassName}
        />
      ) : null}
      <RemoveDocumentConfirmDialog
        isOpen={ctx.pendingDocumentRemove !== null}
        message={
          ctx.pendingDocumentRemove?.kind === 'liberty'
            ? 'Please confirm that you want to remove this Liberty Document.'
            : DEFAULT_EVIDENCE_REMOVE_MESSAGE
        }
        onConfirm={() => {
          if (ctx.pendingDocumentRemove === null) return
          const pending = ctx.pendingDocumentRemove
          ctx.setPendingDocumentRemove(null)
          if (pending.kind === 'liberty') {
            void ctx.documentsHook
              .removeLibertyDocument(pending.id)
              .then(() => showSuccess('Liberty Document removed successfully'))
              .catch(showApiError)
            return
          }
          void ctx.documentsHook
            .removeDefaultFile(pending.id)
            .then(() => showSuccess('Document removed successfully'))
            .catch(showApiError)
        }}
        onCancel={() => ctx.setPendingDocumentRemove(null)}
      />
      <PolicyDeclarationFilesPopup
        isOpen={ctx.policyFilesPopup !== null}
        onClose={() => ctx.setPolicyFilesPopup(null)}
        files={ctx.policyFilesPopup ?? []}
      />

    </>
  )
}
